import { cn } from '@repo/lib/cn';
import { cva } from 'class-variance-authority';
import { useImperativeHandle, useRef } from 'react';

import { VideoPlayerControls } from '@/components/ui/media-player/common/VideoControls';

import type { VideoPlayerProps } from '.';

import { MediaProvider, useMediaStore, VideoPlayerOverlay } from '.';

export const videoPlayerVariants = cva('group @container relative w-full overflow-hidden bg-black', {
  variants: {
    size: {
      sm: 'max-w-md',
      default: 'max-w-2xl',
      lg: 'max-w-4xl',
      full: 'w-full',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export function VideoPlayer(videoPlayerProps: VideoPlayerProps) {
  const { className, size, src, poster, showControls = true, autoHide = true, ref, children, containerRef, containerStyle, ...props } = videoPlayerProps;

  const videoReference = useRef<HTMLVideoElement>(null);
  const containerReference = useRef<HTMLDivElement>(null);
  const mediaStore = useMediaStore(videoReference, containerReference);

  useImperativeHandle(ref, () => videoReference.current!, []);
  useImperativeHandle(containerRef, () => containerReference.current!, []);

  return (
    <MediaProvider store={mediaStore}>
      <div ref={containerReference} className={cn(videoPlayerVariants({ size }), className)} style={containerStyle} tabIndex={0}>
        <video ref={videoReference} src={src} poster={poster} className="h-full w-full object-contain" onClick={mediaStore.togglePlay} {...props} />
        {showControls &&
          (children || (
            <>
              <VideoPlayerControls autoHide={autoHide} />
              <VideoPlayerOverlay autoHide={autoHide} />
            </>
          ))}
      </div>
    </MediaProvider>
  );
}
